import { drawBrandMark } from "@/lib/canvasBrand";
import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { publicOrigin } from "@/lib/publicOrigin";
import { Button } from "@/components/ui/button";
import { Download, Share2, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import { avatarSrc } from "@/lib/defaults";
import { resolveMediaUrl } from "@/components/FileUpload";
import { proxyPeaksUrl } from "@/components/chat/ChatMedia";
import { fetchAudioPeaks } from "@/lib/audioPeaks";
import { toShareLink } from "@/lib/share";
import { downloadPoster, sharePoster } from "@/lib/posterShare";
import { loadCanvasImage } from "@/lib/canvasImage";

const W = 1080, H = 1080;

const THEMES = [
  { key: "foret", name: "Forêt", swatch: "#1c3a2c", stops: [[0, "#1c3a2c"], [0.55, "#0E1A14"], [1, "#070d0a"]] },
  { key: "royal", name: "Royal", swatch: "#3a1f52", stops: [[0, "#3a1f52"], [0.55, "#1b0f2b"], [1, "#0a0512"]] },
  { key: "nuit", name: "Nuit", swatch: "#1e2a4a", stops: [[0, "#1e2a4a"], [0.55, "#0d1226"], [1, "#05070f"]] },
  { key: "corail", name: "Corail", swatch: "#8a3c26", stops: [[0, "#8a3c26"], [0.55, "#3a1a10"], [1, "#160805"]] },
];

const fmtDur = (s) => {
  const n = Math.max(0, Math.round(Number(s) || 0));
  const m = Math.floor(n / 60), r = n % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
};

const wrapCentered = (ctx, text, cx, y, maxWidth, lineHeight, maxLines) => {
  const words = String(text || "").split(" ");
  let line = ""; const lines = [];
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) { lines.push(line); line = w; }
    else line = test;
  }
  if (line) lines.push(line);
  const shown = lines.slice(0, maxLines);
  shown.forEach((ln, i) => ctx.fillText(ln, cx, y + i * lineHeight));
  return shown.length;
};

const loadImg = (url, cors) => loadCanvasImage(url, cors);

// Branded 1:1 share card for an audio post (waveform motif + author + duration).
export const AudioSharePoster = ({ open, onOpenChange, post, t }) => {
  const canvasRef = useRef(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [building, setBuilding] = useState(true);
  const [themeKey, setThemeKey] = useState("foret");
  const [audioDur, setAudioDur] = useState(Number(post?.audio_duration) || 0);
  const [peaks, setPeaks] = useState(null);

  const tt = t || ((k, d) => d);
  const author = post?.author || {};
  const authorName = author?.name || "Yalola";
  const shareUrl = post ? `${publicOrigin()}/post/${post.id}` : "";
  const shareLink = shareUrl ? toShareLink(shareUrl) : "";
  const snippet = (post?.content || "").trim();
  const shareText = `🎧 ${authorName} ${tt("audio.shared_on", "sur Yalola")} 🙏${snippet ? `\n\n${snippet.slice(0, 160)}` : ""}`;

  // audio_duration isn't persisted server-side → read it from the audio metadata.
  useEffect(() => {
    if (!open || !post?.audio_url) return undefined;
    if (Number(post?.audio_duration) > 0) { setAudioDur(Number(post.audio_duration)); return undefined; }
    let cancelled = false;
    const a = document.createElement("audio");
    a.preload = "metadata"; a.crossOrigin = "anonymous";
    const onDur = () => {
      if (!cancelled && isFinite(a.duration) && a.duration > 0) {
        a.removeEventListener("loadedmetadata", onDur);
        a.removeEventListener("durationchange", onDur);
        setAudioDur(a.duration);
      }
    };
    a.addEventListener("loadedmetadata", onDur);
    a.addEventListener("durationchange", onDur);
    a.src = resolveMediaUrl(post.audio_url);
    return () => { cancelled = true; a.removeEventListener("loadedmetadata", onDur); a.removeEventListener("durationchange", onDur); };
  }, [open, post?.id, post?.audio_url, post?.audio_duration]);

  // Real waveform: decode the audio file into normalised peaks (same-origin proxy
  // for CORS). Falls back to the stylised motif when decoding isn't possible.
  useEffect(() => {
    if (!open || !post?.audio_url) return undefined;
    let cancelled = false;
    // Lot 684 : forme d'onde servie par le serveur (plus de fichier entier décodé sur le téléphone).
    fetchAudioPeaks(proxyPeaksUrl(post.audio_url))
      .then((r) => { if (!cancelled && Array.isArray(r?.peaks) && r.peaks.length) setPeaks(r.peaks); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, post?.id, post?.audio_url]);

  useEffect(() => {
    if (!open || !post) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) { setBuilding(false); return undefined; }
    let cancelled = false;
    setBuilding(true); setImgUrl(null);

    const paint = (ctx, avatarImg) => {
      const th = THEMES.find((x) => x.key === themeKey) || THEMES[0];
      const g = ctx.createLinearGradient(0, 0, W, H);
      th.stops.forEach(([o, c]) => g.addColorStop(o, c));
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = "rgba(224,166,60,0.10)";
      ctx.beginPath(); ctx.arc(W * 0.88, H * 0.12, 240, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(W * 0.1, H * 0.9, 200, 0, Math.PI * 2); ctx.fill();

      // Author row (avatar + name)
      const ax = 90, ay = 120, ar = 54;
      if (avatarImg) {
        ctx.save();
        ctx.beginPath(); ctx.arc(ax + ar, ay + ar, ar, 0, Math.PI * 2); ctx.closePath(); ctx.clip();
        ctx.drawImage(avatarImg, ax, ay, ar * 2, ar * 2);
        ctx.restore();
      } else {
        ctx.fillStyle = "#2D5A4C";
        ctx.beginPath(); ctx.arc(ax + ar, ay + ar, ar, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#fff"; ctx.font = "bold 56px Outfit, Arial, sans-serif";
        ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.fillText((authorName || "?").trim().charAt(0).toUpperCase(), ax + ar, ay + ar + 4);
      }
      ctx.lineWidth = 5; ctx.strokeStyle = "#E0A63C";
      ctx.beginPath(); ctx.arc(ax + ar, ay + ar, ar, 0, Math.PI * 2); ctx.stroke();
      ctx.textAlign = "left"; ctx.textBaseline = "top";
      ctx.fillStyle = "#fff"; ctx.font = "600 46px Outfit, Arial, sans-serif";
      ctx.fillText(String(authorName).slice(0, 22), ax + ar * 2 + 26, ay + 12);
      ctx.fillStyle = "rgba(255,255,255,0.8)"; ctx.font = "32px Inter, Arial, sans-serif";
      ctx.fillText(tt("audio.shared_audio", "a partagé un audio 🎧"), ax + ar * 2 + 26, ay + 66);

      // Waveform (real peaks when decoded, else a stylised motif) + play disc
      const cx = W / 2, cy = H / 2 - 20;
      ctx.fillStyle = "#E0A63C";
      ctx.beginPath(); ctx.arc(cx - 300, cy, 66, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#0E1A14";
      ctx.beginPath(); ctx.moveTo(cx - 322, cy - 32); ctx.lineTo(cx - 322, cy + 32); ctx.lineTo(cx - 274, cy); ctx.closePath(); ctx.fill();
      const bars = (peaks && peaks.length) ? peaks.length : 34;
      const regionW = 560, startX = cx - 230, slot = regionW / bars;
      const bw = Math.max(5, slot * 0.62), maxH = 190;
      for (let i = 0; i < bars; i++) {
        const amp = peaks ? peaks[i] : Math.abs(Math.sin(i * 1.7) * Math.cos(i * 0.6));
        const bh = 24 + amp * maxH;
        const x = startX + i * slot;
        ctx.fillStyle = i < bars * 0.42 ? "#E0A63C" : "rgba(255,255,255,0.32)";
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(x, cy - bh / 2, bw, bh, Math.min(4, bw / 2)); else ctx.rect(x, cy - bh / 2, bw, bh);
        ctx.fill();
      }

      // Duration (only when known — audio_duration isn't always available)
      if (audioDur > 0) {
        ctx.textAlign = "center"; ctx.textBaseline = "top";
        ctx.fillStyle = "rgba(255,255,255,0.9)"; ctx.font = "bold 40px Outfit, Arial, sans-serif";
        ctx.fillText(`⏱ ${fmtDur(audioDur)}`, cx, cy + 130);
      }

      // Snippet (optional)
      if (snippet) {
        ctx.fillStyle = "rgba(255,255,255,0.92)"; ctx.font = "34px Inter, Arial, sans-serif";
        wrapCentered(ctx, `“${snippet}”`, cx, cy + 200, W - 220, 46, 3);
      }

      // Brand footer
      drawBrandMark(ctx, 150, H - 130, 46);
      ctx.textAlign = "left"; ctx.textBaseline = "top";
      ctx.fillStyle = "#fff"; ctx.font = "bold 48px Outfit, Arial, sans-serif";
      ctx.fillText("Yalola", 210, H - 154);
      ctx.fillStyle = "rgba(255,255,255,0.72)"; ctx.font = "28px Inter, Arial, sans-serif";
      ctx.fillText(tt("story.brand_tagline", "Le réseau chrétien qui rassemble"), 210, H - 104);
    };

    const finish = () => {
      try { canvas.toBlob((b) => { if (!cancelled) { if (b) setImgUrl(URL.createObjectURL(b)); setBuilding(false); } }, "image/png"); return true; }
      catch { return false; }
    };

    (async () => {
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext("2d");
      const avatarImg = await loadImg(avatarSrc(author), true);
      if (cancelled) return;
      paint(ctx, avatarImg);
      if (!finish()) {
        // Cross-origin avatar tainted the canvas → redraw with initials only.
        ctx.clearRect(0, 0, W, H);
        paint(ctx, null);
        finish();
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, post?.id, themeKey, audioDur, peaks]);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(`${shareText}\n${shareLink}`); toast.success(tt("story.copied", "Copié — collez où vous voulez")); }
    catch { toast.error(tt("story.copy_failed", "Copie impossible")); }
  };
  const download = () => downloadPoster({ url: imgUrl, fileName: "yalola-audio.png", t: tt });
  const doNativeShare = () => sharePoster({ url: imgUrl, text: shareText, link: shareLink, fileName: "yalola-audio.png", copiedMessage: tt("story.copied", "Copié — collez où vous voulez"), t: tt });

  return (
    <>
      <canvas ref={canvasRef} style={{ display: "none" }} aria-hidden="true" />
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-sm" data-testid="audio-share-modal">
          <DialogHeader>
            <DialogTitle>{tt("audio.share_title", "Partager l'audio")}</DialogTitle>
            <DialogDescription>{tt("audio.share_desc", "Partagez cette carte sur WhatsApp, Instagram…")}</DialogDescription>
          </DialogHeader>
          <div className="rounded-xl overflow-hidden border" style={{ borderColor: "var(--yalola-border)", aspectRatio: "1 / 1" }}>
            {building && <div className="w-full h-full grid place-items-center"><Loader2 className="animate-spin" style={{ color: "var(--yalola-primary)" }} /></div>}
            {!building && imgUrl && <img src={imgUrl} alt="" className="w-full block" data-testid="audio-share-image" />}
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="audio-share-themes">
            {THEMES.map((th) => (
              <button
                key={th.key}
                onClick={() => setThemeKey(th.key)}
                title={th.name}
                className="h-8 w-8 rounded-full border-2 transition-transform hover:scale-110"
                style={{ background: th.swatch, borderColor: themeKey === th.key ? "var(--yalola-primary)" : "transparent" }}
                data-testid={`audio-share-theme-${th.key}`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            <Button onClick={doNativeShare} disabled={building} className="flex-1 pill-primary rounded-full" data-testid="audio-share-share"><Share2 size={16} className="mr-1.5" /> {tt("common.share", "Partager")}</Button>
            <Button onClick={download} disabled={building} variant="outline" className="rounded-full" title={tt("common.download", "Télécharger")} data-testid="audio-share-download"><Download size={16} /></Button>
            <Button onClick={copyLink} variant="outline" className="rounded-full" title={tt("common.copy_link", "Copier")} data-testid="audio-share-copy"><Copy size={16} /></Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AudioSharePoster;
